import type { ReactNode } from "react";

import {
  DotsThreeIcon,
  GearIcon,
  LayoutIcon,
  PlusIcon,
  SidebarSimpleIcon,
  SquaresFourIcon,
} from "@phosphor-icons/react";
import {
  Box,
  Button,
  DropdownMenu,
  Flex,
  IconButton,
  Tabs,
  Text,
  Tooltip,
} from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import { NewWorkspaceMenuPresentation } from "./NewWorkspaceMenu.pres";
import type { WorkspaceTemplateId } from "./shared/workspace/workspaceTemplates";
import type { WorkspaceTab, WorkspaceTabId } from "./Workspace.controller";
import { workspaceInitials } from "./workspaceNav.utils";

export type WorkspaceNavMode = "top" | "left";

/**
 * Stable UI-only value used for the Overview tab trigger and Tabs.Root/Content.
 * It is intentionally distinct from all workspace IDs (which are UUIDs) so it
 * can participate in Radix's Tabs context without polluting workspace semantics.
 */
export const OVERVIEW_NAV_VALUE = "overview" as const;

/** Initials shown in the collapsed left rail. */
const SETTINGS_INITIALS = workspaceInitials("Settings");
const OVERVIEW_INITIALS = workspaceInitials("Overview");

// ── Shared props present in both modes ──────────────────────────────────────

interface WorkspaceNavSharedProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  activeTab: WorkspaceTabId | typeof OVERVIEW_NAV_VALUE;
  tabs: ReadonlyArray<WorkspaceTab>;
  canCloseTab: boolean;
  onSelectTab: (tabId: WorkspaceTabId) => void;
  selectOverview: () => void;
  onCreateWorkspace: (templateId?: WorkspaceTemplateId) => void;
  onDuplicateWorkspace: (tabId: WorkspaceTabId) => void;
  onOpenWorkspace: (tabId: WorkspaceTabId) => void;
  onRequestClose: (tabId: WorkspaceTabId) => void;
  onBeginRename: (tab: WorkspaceTab) => void;
  onOpenSettings: () => void;
  /**
   * Account-level controls for the trailing edge of the nav. Passed as an
   * element so this presenter stays free of the data hooks they need.
   */
  accountActions?: ReactNode;
}

// ── Discriminated presenter props ────────────────────────────────────────────

/**
 * Top-mode props. All drag/measurement props from the controller are required
 * so the presenter can forward them without silent no-op fallbacks.
 */
export interface WorkspaceNavTopPresentationProps
  extends WorkspaceNavSharedProps {
  mode: "top";
  draggingTabId: WorkspaceTabId | null;
  /** Measures the space the tab strip may occupy before `More` takes over. */
  stripRef: (node: HTMLDivElement | null) => void;
  /** Tabs rendered as triggers; the remainder moves into the `More` menu. */
  visibleTabs: ReadonlyArray<WorkspaceTab>;
  overflowTabs: ReadonlyArray<WorkspaceTab>;
  /** Measures the pinned Overview tab, which never overflows. */
  registerOverviewTrigger: (node: HTMLElement | null) => void;
  registerTabTrigger: (tabId: WorkspaceTabId, node: HTMLElement | null) => void;
  dropEdge: (tabId: WorkspaceTabId) => "left" | "right" | null;
  onTabDragStart: (tabId: WorkspaceTabId, event: React.DragEvent) => void;
  onTabDragEnd: () => void;
  onTabDragOver: (tabId: WorkspaceTabId, event: React.DragEvent) => void;
  onTabDragLeave: (tabId: WorkspaceTabId) => void;
  onTabDrop: (tabId: WorkspaceTabId, event: React.DragEvent) => void;
}

/**
 * Left-mode props. Excludes all top-mode drag/measurement props; the left
 * rail has no drag reorder and owns its toolbar slot inside the board header.
 */
export interface WorkspaceNavLeftPresentationProps
  extends WorkspaceNavSharedProps {
  mode: "left";
}

/** Discriminated union — TypeScript narrows to the correct arm on `mode`. */
export type WorkspaceNavPresentationProps =
  | WorkspaceNavTopPresentationProps
  | WorkspaceNavLeftPresentationProps;

// ── Internal sub-component interfaces ────────────────────────────────────────

interface TabActionsMenuProps {
  tab: WorkspaceTab;
  canCloseTab: boolean;
  onBeginRename: (tab: WorkspaceTab) => void;
  onDuplicateWorkspace: (tabId: WorkspaceTabId) => void;
  onOpenWorkspace: (tabId: WorkspaceTabId) => void;
  onRequestClose: (tabId: WorkspaceTabId) => void;
  trigger: ReactNode;
}

const TabActionsMenu = ({
  tab,
  canCloseTab,
  onBeginRename,
  onDuplicateWorkspace,
  onOpenWorkspace,
  onRequestClose,
  trigger,
}: TabActionsMenuProps) => (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger>{trigger}</DropdownMenu.Trigger>
    <DropdownMenu.Content align="end" size="1" variant="soft">
      <DropdownMenu.Item onSelect={() => onBeginRename(tab)}>
        Rename
      </DropdownMenu.Item>
      <DropdownMenu.Item onSelect={() => onDuplicateWorkspace(tab.id)}>
        Duplicate
      </DropdownMenu.Item>
      <DropdownMenu.Item onSelect={() => onOpenWorkspace(tab.id)}>
        New Window
      </DropdownMenu.Item>
      <DropdownMenu.Separator />
      <DropdownMenu.Item
        color="red"
        disabled={!canCloseTab}
        onSelect={() => onRequestClose(tab.id)}
      >
        Delete
      </DropdownMenu.Item>
    </DropdownMenu.Content>
  </DropdownMenu.Root>
);

/**
 * Visible triggers get their selection from Radix's Tabs context; `activeTab`
 * weights the active icon.
 */
type TopModeProps = Omit<
  WorkspaceNavTopPresentationProps,
  "mode" | "collapsed" | "onToggleCollapsed" | "selectOverview" | "tabs"
>;

/** Workspaces that did not fit, reachable through the `More` menu. */
type OverflowMenuProps = Pick<
  TopModeProps,
  | "overflowTabs"
  | "activeTab"
  | "canCloseTab"
  | "onSelectTab"
  | "onBeginRename"
  | "onDuplicateWorkspace"
  | "onOpenWorkspace"
  | "onRequestClose"
>;

const OverflowMenu = ({
  overflowTabs,
  activeTab,
  canCloseTab,
  onSelectTab,
  onBeginRename,
  onDuplicateWorkspace,
  onOpenWorkspace,
  onRequestClose,
}: OverflowMenuProps) => (
  // Ghost Button's negative margin would otherwise start inside the last tab.
  <Flex align="center" ml="3" className="shrink-0">
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Button
          type="button"
          size="1"
          variant="ghost"
          color="gray"
          aria-label={`More workspaces (${overflowTabs.length})`}
          data-testid="workspace-tabs-more"
        >
          <Text size="2">More ({overflowTabs.length})</Text>
          <DropdownMenu.TriggerIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start" size="1" variant="soft">
        {/* Submenu keeps actions reachable without opening the workspace. */}
        {overflowTabs.map((tab) => (
          <DropdownMenu.Sub key={tab.id}>
            <DropdownMenu.SubTrigger>
              <Flex align="center" gap="2">
                <LayoutIcon size={14} aria-hidden />
                <Text size="2" className="max-w-[14rem] truncate">
                  {tab.label}
                </Text>
              </Flex>
            </DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent>
              <DropdownMenu.Item
                disabled={tab.id === activeTab}
                onSelect={() => onSelectTab(tab.id)}
              >
                Open
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item onSelect={() => onBeginRename(tab)}>
                Rename
              </DropdownMenu.Item>
              <DropdownMenu.Item onSelect={() => onDuplicateWorkspace(tab.id)}>
                Duplicate
              </DropdownMenu.Item>
              <DropdownMenu.Item onSelect={() => onOpenWorkspace(tab.id)}>
                New Window
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                color="red"
                disabled={!canCloseTab}
                onSelect={() => onRequestClose(tab.id)}
              >
                Delete
              </DropdownMenu.Item>
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  </Flex>
);

const TopModeNav = ({
  activeTab,
  canCloseTab,
  onSelectTab,
  onCreateWorkspace,
  onDuplicateWorkspace,
  onOpenWorkspace,
  onRequestClose,
  onBeginRename,
  onOpenSettings,
  accountActions,
  draggingTabId,
  stripRef,
  visibleTabs,
  overflowTabs,
  registerOverviewTrigger,
  registerTabTrigger,
  dropEdge,
  onTabDragStart,
  onTabDragEnd,
  onTabDragOver,
  onTabDragLeave,
  onTabDrop,
}: TopModeProps) => (
  <Flex
    align="stretch"
    gap="3"
    px="3"
    className="shrink-0 border-[color:var(--gray-a5)] border-b [background-color:var(--color-panel-solid)]"
  >
    <Flex
      ref={stripRef}
      data-testid="workspace-tabs-strip"
      align="stretch"
      gap="1"
      className="min-w-0 grow overflow-hidden"
    >
      {/* `min-w-0` yields to the More trigger instead of clipping it. */}
      <Tabs.List color="blue" aria-label="Workspaces" className="min-w-0">
        <Tabs.Trigger ref={registerOverviewTrigger} value={OVERVIEW_NAV_VALUE}>
          <Flex align="center" gap="2">
            <SquaresFourIcon
              size={16}
              weight={activeTab === OVERVIEW_NAV_VALUE ? "fill" : "regular"}
              aria-hidden
            />
            <Text size="2">Overview</Text>
          </Flex>
        </Tabs.Trigger>
        {visibleTabs.map((tab) => (
          <div
            key={tab.id}
            ref={(node) => registerTabTrigger(tab.id, node)}
            className={cn(
              "relative flex shrink-0 items-center justify-start",
              // Radix draws its active indicator on the trigger only, which
              // stops short of the actions menu. Span it across the wrapper
              // so the underline runs the full width of the tab.
              tab.id === activeTab &&
                "after:absolute after:inset-x-0 after:bottom-0 after:h-[2px] after:bg-[var(--accent-indicator)] after:content-['']",
              draggingTabId === tab.id && "opacity-50",
              dropEdge(tab.id) === "left" &&
                "shadow-[inset_2px_0_0_0_var(--accent-9)]",
              dropEdge(tab.id) === "right" &&
                "shadow-[inset_-2px_0_0_0_var(--accent-9)]",
            )}
          >
            <Tabs.Trigger
              value={tab.id}
              data-drop-edge={dropEdge(tab.id) ?? undefined}
              onDragOver={(event) => onTabDragOver(tab.id, event)}
              onDragLeave={() => onTabDragLeave(tab.id)}
              onDrop={(event) => onTabDrop(tab.id, event)}
            >
              <Flex
                align="center"
                gap="2"
                className="min-w-0"
                draggable
                onDragStart={(event) => onTabDragStart(tab.id, event)}
                onDragEnd={onTabDragEnd}
              >
                <LayoutIcon
                  size={16}
                  weight={tab.id === activeTab ? "fill" : "regular"}
                  aria-hidden
                />
                <Text size="2" className="min-w-0 max-w-[180px] truncate">
                  {tab.label}
                </Text>
              </Flex>
            </Tabs.Trigger>
            <TabActionsMenu
              tab={tab}
              canCloseTab={canCloseTab}
              onBeginRename={onBeginRename}
              onDuplicateWorkspace={onDuplicateWorkspace}
              onOpenWorkspace={onOpenWorkspace}
              onRequestClose={onRequestClose}
              trigger={
                <IconButton
                  type="button"
                  size="1"
                  variant="ghost"
                  color="gray"
                  aria-label={`Workspace actions for ${tab.label}`}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                >
                  <DotsThreeIcon size={14} aria-hidden />
                </IconButton>
              }
            />
          </div>
        ))}
      </Tabs.List>

      {overflowTabs.length > 0 ? (
        <OverflowMenu
          overflowTabs={overflowTabs}
          activeTab={activeTab}
          canCloseTab={canCloseTab}
          onSelectTab={onSelectTab}
          onBeginRename={onBeginRename}
          onDuplicateWorkspace={onDuplicateWorkspace}
          onOpenWorkspace={onOpenWorkspace}
          onRequestClose={onRequestClose}
        />
      ) : null}
    </Flex>

    <Flex align="center" className="shrink-0">
      <NewWorkspaceMenuPresentation
        align="end"
        onCreateWorkspace={onCreateWorkspace}
        trigger={
          <Tooltip content="New workspace">
            <DropdownMenu.Trigger>
              <IconButton
                type="button"
                size="2"
                variant="ghost"
                color="gray"
                aria-label="New workspace"
              >
                <PlusIcon size={16} />
              </IconButton>
            </DropdownMenu.Trigger>
          </Tooltip>
        }
      />
    </Flex>

    <Flex align="center" gap="2" className="shrink-0">
      {accountActions}
      <Tooltip content="App settings">
        <IconButton
          type="button"
          size="2"
          variant="ghost"
          color="gray"
          aria-label="App settings"
          onClick={onOpenSettings}
        >
          <GearIcon size={16} aria-hidden />
        </IconButton>
      </Tooltip>
    </Flex>
  </Flex>
);

/**
 * Rail row geometry. Every row keeps the same height, radius, padding and
 * leading-edge alignment in both states so selecting a workspace repaints the
 * row instead of reflowing the rail — Radix's `soft`/`ghost` variant swap
 * changes box metrics.
 *
 * `box-border` and `m-0` undo the ghost variant's `content-box` sizing and its
 * negative margins, which otherwise make the row paint ~8px wider and ~4px
 * taller than its box — enough to visibly overhang the New Workspace button
 * below it.
 */
const NAV_ROW_BASE =
  "m-0 box-border h-8 w-full justify-start rounded-[6px] px-[10px] transition-colors motion-reduce:transition-none";

/**
 * The button paints the row surface, which now spans the row exactly. Hover
 * comes from `group-hover` for rows with an actions trigger: the trigger floats
 * over the button rather than inside it, so pointing at the dots never hovers
 * the button itself.
 */
const navRowTone = (active: boolean) =>
  active
    ? "[background-color:var(--gray-a3)] [color:var(--gray-12)]"
    : cn(
        "[color:var(--gray-a11)]",
        "hover:[background-color:var(--gray-a3)] hover:[color:var(--gray-12)]",
        "group-hover:[background-color:var(--gray-a3)] group-hover:[color:var(--gray-12)]",
      );

interface NavRailRowProps {
  collapsed: boolean;
  active: boolean;
  label: string;
  /** Short form shown instead of the label in the collapsed rail. */
  initials: string;
  icon: ReactNode;
  ariaLabel: string;
  onSelect: () => void;
  /** Trailing actions menu, expanded rail only. */
  actions?: ReactNode;
}

const NavRailRow = ({
  collapsed,
  active,
  label,
  initials,
  icon,
  ariaLabel,
  onSelect,
  actions,
}: NavRailRowProps) => {
  const selectButton = (
    <Button
      type="button"
      size="2"
      variant="ghost"
      color="gray"
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      onClick={onSelect}
      className={cn(
        NAV_ROW_BASE,
        navRowTone(active),
        // The actions trigger floats over the button's trailing edge, so keep
        // the label clear of it — otherwise a long name runs underneath.
        actions && !collapsed && "pr-[32px]",
      )}
    >
      <Flex align="center" gap={collapsed ? "1" : "2"} className="min-w-0">
        {icon}
        <Text
          size={collapsed ? "1" : "2"}
          weight={active ? "medium" : "regular"}
          className="truncate"
        >
          {collapsed ? initials : label}
        </Text>
      </Flex>
    </Button>
  );

  if (collapsed) {
    return <Tooltip content={label}>{selectButton}</Tooltip>;
  }

  if (!actions) {
    return selectButton;
  }

  // `group` keeps the actions trigger in the DOM (keyboard/SR reachable) while
  // it stays visually hidden until the row is hovered, focused, active, or its
  // menu is open, and drives the row's hover surface.
  return (
    <Box
      position="relative"
      className="group w-full"
      data-workspace-active={active ? "true" : undefined}
    >
      {selectButton}
      {/*
       * Floating the trigger over the button keeps it inside the painted row
       * surface, so the hover fill runs behind the dots instead of stopping
       * short of them. Out of flow it also cannot be pushed past the rail edge
       * by a long label.
       */}
      <Flex position="absolute" top="0" bottom="0" right="1" align="center">
        {actions}
      </Flex>
    </Box>
  );
};

/**
 * Left-mode rail. No drag reorder; owns active state locally since it does
 * not sit inside a Radix Tabs.Root.
 */
type LeftModeProps = Omit<WorkspaceNavLeftPresentationProps, "mode">;

const LeftModeNav = ({
  collapsed,
  onToggleCollapsed,
  activeTab,
  tabs,
  canCloseTab,
  onSelectTab,
  selectOverview,
  onCreateWorkspace,
  onDuplicateWorkspace,
  onOpenWorkspace,
  onRequestClose,
  onBeginRename,
  onOpenSettings,
  accountActions,
}: LeftModeProps) => {
  const width = collapsed ? "w-[88px]" : "w-[220px]";
  const isOverviewActive = activeTab === OVERVIEW_NAV_VALUE;

  return (
    <Flex
      asChild
      direction="column"
      justify="between"
      alignSelf="stretch"
      minHeight="0"
      flexShrink="0"
      className={cn(
        "border-[color:var(--gray-a5)] border-r",
        "[background-color:var(--color-panel-solid)]",
        "transition-[width] duration-[160ms] ease-out motion-reduce:transition-none",
        width,
      )}
    >
      <nav aria-label="Workspaces">
        <Flex
          data-testid="workspace-nav-list"
          direction="column"
          gap="1"
          p="3"
          flexGrow="1"
          minHeight="0"
          overflowX="hidden"
          overflowY="auto"
        >
          {/* Pinned Overview destination — no actions menu */}
          <NavRailRow
            collapsed={collapsed}
            active={isOverviewActive}
            label="Overview"
            initials={OVERVIEW_INITIALS}
            icon={
              <SquaresFourIcon
                size={18}
                weight={isOverviewActive ? "fill" : "regular"}
                aria-hidden
              />
            }
            ariaLabel="Overview"
            onSelect={selectOverview}
          />
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <NavRailRow
                key={tab.id}
                collapsed={collapsed}
                active={isActive}
                label={tab.label}
                initials={workspaceInitials(tab.label)}
                icon={
                  <LayoutIcon
                    size={18}
                    weight={isActive ? "fill" : "regular"}
                    aria-hidden
                  />
                }
                ariaLabel={`Select workspace ${tab.label}`}
                onSelect={() => onSelectTab(tab.id)}
                actions={
                  <TabActionsMenu
                    tab={tab}
                    canCloseTab={canCloseTab}
                    onBeginRename={onBeginRename}
                    onDuplicateWorkspace={onDuplicateWorkspace}
                    onOpenWorkspace={onOpenWorkspace}
                    onRequestClose={onRequestClose}
                    trigger={
                      <IconButton
                        type="button"
                        size="1"
                        variant="ghost"
                        color="gray"
                        aria-label={`Workspace actions for ${tab.label}`}
                        className={cn(
                          "transition-opacity motion-reduce:transition-none",
                          // Focus-visible keeps the trigger reachable by keyboard
                          // even when the row is at rest; `data-[state=open]`
                          // keeps it visible while its menu is open. The active
                          // row always shows the actions so the primary target
                          // is discoverable.
                          isActive
                            ? "opacity-100"
                            : "opacity-0 focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100",
                        )}
                      >
                        <DotsThreeIcon size={14} aria-hidden />
                      </IconButton>
                    }
                  />
                }
              />
            );
          })}

          {/* `mt` sets the create action apart from the workspace rows it follows. */}
          {collapsed ? (
            <NewWorkspaceMenuPresentation
              align="start"
              onCreateWorkspace={onCreateWorkspace}
              trigger={
                <Tooltip content="New workspace">
                  <DropdownMenu.Trigger>
                    <IconButton
                      type="button"
                      size="2"
                      variant="outline"
                      color="yellow"
                      aria-label="New workspace"
                      mt="4"
                      className="h-8 w-full rounded-[6px]"
                    >
                      <PlusIcon size={16} aria-hidden />
                    </IconButton>
                  </DropdownMenu.Trigger>
                </Tooltip>
              }
            />
          ) : (
            <NewWorkspaceMenuPresentation
              align="start"
              onCreateWorkspace={onCreateWorkspace}
              trigger={
                <DropdownMenu.Trigger>
                  <Button
                    type="button"
                    size="2"
                    variant="outline"
                    color="yellow"
                    mt="4"
                    className="h-8 w-full justify-start rounded-[6px] px-[10px]"
                  >
                    <Flex align="center" gap="2">
                      <PlusIcon size={16} aria-hidden />
                      <Text size="2">New Workspace</Text>
                    </Flex>
                  </Button>
                </DropdownMenu.Trigger>
              }
            />
          )}
        </Flex>

        <Flex
          data-testid="workspace-nav-footer"
          direction="column"
          p="3"
          flexShrink="0"
        >
          <NavRailRow
            collapsed={collapsed}
            active={false}
            label="Settings"
            initials={SETTINGS_INITIALS}
            icon={<GearIcon size={18} aria-hidden />}
            ariaLabel="App settings"
            onSelect={onOpenSettings}
          />

          {accountActions ? (
            <Flex mt="2" justify="start">
              {accountActions}
            </Flex>
          ) : null}

          <Flex
            data-testid="workspace-nav-footer-divider"
            mt="3"
            pt="3"
            className="border-[color:var(--gray-a5)] border-t"
          >
            {/* justify prop alone drives alignment; no duplicate Tailwind class */}
            <Flex
              data-testid="workspace-nav-collapse-row"
              justify={collapsed ? "start" : "end"}
              className="w-full"
            >
              <Tooltip
                content={
                  collapsed ? "Expand navigation" : "Collapse navigation"
                }
              >
                <IconButton
                  type="button"
                  variant="ghost"
                  color="gray"
                  aria-label={
                    collapsed ? "Expand navigation" : "Collapse navigation"
                  }
                  onClick={onToggleCollapsed}
                >
                  <SidebarSimpleIcon size={18} aria-hidden />
                </IconButton>
              </Tooltip>
            </Flex>
          </Flex>
        </Flex>
      </nav>
    </Flex>
  );
};

export const WorkspaceNavPresentation = (
  props: WorkspaceNavPresentationProps,
) => {
  if (props.mode === "top") {
    return (
      <TopModeNav
        activeTab={props.activeTab}
        onSelectTab={props.onSelectTab}
        canCloseTab={props.canCloseTab}
        onCreateWorkspace={props.onCreateWorkspace}
        onDuplicateWorkspace={props.onDuplicateWorkspace}
        onOpenWorkspace={props.onOpenWorkspace}
        onRequestClose={props.onRequestClose}
        onBeginRename={props.onBeginRename}
        onOpenSettings={props.onOpenSettings}
        accountActions={props.accountActions}
        draggingTabId={props.draggingTabId}
        stripRef={props.stripRef}
        visibleTabs={props.visibleTabs}
        overflowTabs={props.overflowTabs}
        registerOverviewTrigger={props.registerOverviewTrigger}
        registerTabTrigger={props.registerTabTrigger}
        dropEdge={props.dropEdge}
        onTabDragStart={props.onTabDragStart}
        onTabDragEnd={props.onTabDragEnd}
        onTabDragOver={props.onTabDragOver}
        onTabDragLeave={props.onTabDragLeave}
        onTabDrop={props.onTabDrop}
      />
    );
  }
  return (
    <LeftModeNav
      collapsed={props.collapsed}
      onToggleCollapsed={props.onToggleCollapsed}
      activeTab={props.activeTab}
      tabs={props.tabs}
      canCloseTab={props.canCloseTab}
      onSelectTab={props.onSelectTab}
      selectOverview={props.selectOverview}
      onCreateWorkspace={props.onCreateWorkspace}
      onDuplicateWorkspace={props.onDuplicateWorkspace}
      onOpenWorkspace={props.onOpenWorkspace}
      onRequestClose={props.onRequestClose}
      onBeginRename={props.onBeginRename}
      onOpenSettings={props.onOpenSettings}
      accountActions={props.accountActions}
    />
  );
};
